/**
 * The plan's values as a file, and back again.
 *
 * It is the one export that survives Aether: a flat JSON object of LBA cell ids
 * to strings, which is what the PDF is stamped from and what any other tool
 * could read. The names are `Plan als Datei` / `Plan aus Datei` rather than the
 * predecessor's „Daten sichern"/„Daten laden", which collided with the autosave
 * state — a pilot who reads „gespeichert" in the strip and „Daten sichern" in a
 * menu cannot tell which of the two saved their flight.
 *
 * Importing replaces the document, so it asks first, exactly as the overflow
 * menu's version does.
 */

import { useRef, useState } from 'react';

import * as api from '../../api';
import { useT } from '../../i18n';
import { pdfFilename } from '../../pdf';
import { usePlanStore } from '../../planContext';

export function FileCard() {
  const t = useT();
  const store = usePlanStore();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<unknown>(null);

  const save = () => {
    const values = store.collect();
    const blob = new Blob([JSON.stringify(values, null, 1)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = pdfFilename(values).replace(/\.pdf$/, '.json');
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 4000);
  };

  const open = async (file: File) => {
    setError(null);
    try {
      const obj = JSON.parse(await file.text()) as unknown;
      if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
        throw new Error(t('err.notAnObject'));
      }
      const values: Record<string, string> = {};
      for (const [k, v] of Object.entries(obj as Record<string, unknown>)) values[k] = String(v ?? '');
      if (!store.isEmpty() && !window.confirm(t('ed.confirm.import'))) return;
      store.loadValues(values, true);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <section className="aex-card">
      <div className="aex-head">
        <h2 className="aex-title">{t('export.file.title')}</h2>
      </div>
      <p className="aex-line">{t('export.file.line')}</p>
      <div className="aex-acts">
        <button type="button" className="ae-btn" onClick={save}>
          {t('ed.export')}
        </button>
        <button type="button" className="ae-btn" onClick={() => input.current?.click()}>
          {t('ed.import')}
        </button>
      </div>
      <input
        ref={input}
        type="file"
        hidden
        accept="application/json,.json"
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) void open(f);
          e.target.value = '';
        }}
      />
      {error ? <p className="aex-err">{t('err.file', { error: api.apiMessage(t, error) })}</p> : null}
    </section>
  );
}
